import { useCallback, useEffect, useState } from 'react';
import {
  getCompetitionCandidates, addCompetitionMembers, getDirectoryFilters, assetUrl,
} from '../../services/api';
import { getRumaalColor } from '../../utils/colors';

// Picking a team.
//
// This screen exists for one sentence the client said: "if I lost a competitive
// dancer, I need a boy super junior — I can click and see who is the highest
// rank and pull that person into the team." So division, gender and rank filter
// together, and the default sort is by rank rather than by name.
//
// The filter options come from the student directory rather than a hardcoded
// list, so they only ever offer values that exist in the data.

const FIELD = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

export default function AddDancers({ teamId, onClose, onAdded }) {
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [options, setOptions] = useState({ ranks: [], divisions: [], genders: [] });
  const [filters, setFilters] = useState({ search: '', rank: '', division: '', gender: '' });
  const [chosen, setChosen] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getDirectoryFilters();
        if (!cancelled) setOptions(data);
      } catch {
        // Filters are a convenience; searching by name still works without them.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCompetitionCandidates(teamId, { ...filters, sort: 'rank' });
      setCandidates(data.candidates);
      setTotal(data.total);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load dancers');
    } finally {
      setLoading(false);
    }
  }, [teamId, filters]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const toggle = (key) => setChosen((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const available = candidates.filter((c) => !c.onTeam);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await addCompetitionMembers(teamId, [...chosen]);
      onAdded();
    } catch (addError) {
      setError(addError.response?.data?.error || 'Could not add those dancers');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative h-full w-full overflow-y-auto bg-white shadow-2xl sm:max-w-lg">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-5 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Who is on the team?</h3>
            <button onClick={onClose} className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600">
              Close
            </button>
          </div>

          <input
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            placeholder="Search a name…"
            className={`${FIELD} mt-3 w-full`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Select value={filters.division} onChange={(v) => setFilter('division', v)}
              options={options.divisions} allLabel="Any division" label="Division" />
            <Select value={filters.gender} onChange={(v) => setFilter('gender', v)}
              options={options.genders} allLabel="Any gender" label="Gender" />
            <Select value={filters.rank} onChange={(v) => setFilter('rank', v)}
              options={options.ranks} allLabel="Any rank" label="Rank" />
          </div>

          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Highest rank first{total > candidates.length && ` · showing ${candidates.length} of ${total}`}</span>
            {available.length > 0 && (
              <button
                onClick={() => setChosen(new Set(available.map((c) => c.studentKey)))}
                className="text-purple-600 hover:text-purple-700"
              >
                Select all {available.length} shown
              </button>
            )}
          </div>
        </div>

        <div className="space-y-1.5 p-5">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {!loading && candidates.length === 0 && (
            <p className="text-sm text-gray-500">No dancers match those filters.</p>
          )}

          {candidates.map((candidate) => (
            <button
              key={candidate.studentKey}
              onClick={() => !candidate.onTeam && toggle(candidate.studentKey)}
              disabled={candidate.onTeam}
              className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                candidate.onTeam ? 'border-gray-100 bg-gray-50 opacity-60'
                  : chosen.has(candidate.studentKey) ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
                style={{ boxShadow: `0 0 0 2px ${getRumaalColor(candidate.rumaalRank)}, 0 0 0 3px rgba(0,0,0,.15)` }}
              >
                {candidate.photoUrl
                  ? <img src={assetUrl(candidate.photoUrl)} alt="" className="h-full w-full object-cover" />
                  : <span className="text-[10px] font-semibold text-gray-400">
                      {candidate.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </span>}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">{candidate.name}</span>
                <span className="block truncate text-xs text-gray-500">
                  {[candidate.rumaalRank || 'No rank', candidate.ageDivision || 'No date of birth',
                    candidate.gender, candidate.className].filter(Boolean).join(' · ')}
                </span>
              </span>
              {candidate.onTeam && <span className="shrink-0 text-[11px] text-gray-500">already on</span>}
            </button>
          ))}
        </div>

        {error && <p className="px-5 pb-3 text-sm text-red-600">{error}</p>}

        <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4">
          <span className="text-sm text-gray-600">{chosen.size} chosen</span>
          <button
            onClick={submit}
            disabled={busy || chosen.size === 0}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Adding…' : `Add ${chosen.size} to the team`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Select({ value, onChange, options = [], allLabel, label }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={FIELD} aria-label={label}>
      <option value="">{allLabel}</option>
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}
